<template>
  <div class="h-full w-full bg-steel-100">
    <div class="container-wide-ultra m-auto flex flex-col gap-6">
      <div
        class="m-auto flex gap-4 px-6"
        :class="{
          'max-w-[1700px]': isDesktop,
          'w-full max-w-[750px] flex-col-reverse': !isDesktop,
          'w-full':
            generalStatus.eld !== 'not-started' && !canViewVerifiedPickupScore,
        }"
      >
        <div
          class="flex flex-col gap-2 rounded-lg bg-white p-4 text-left shadow-md"
          :class="{
            'w-4/5': isDesktop,
            'w-full': !isDesktop,
            'w-full':
              generalStatus.eld !== 'not-started' &&
              !canViewVerifiedPickupScore,
          }"
        >
          <p class="w-max text-xl text-steel-900">Find Loads</p>

          <div v-if="!isDesktop && searchCollapsed">
            <div
              class="m-auto mt-2 flex max-w-[500px] cursor-pointer justify-center gap-3 rounded-lg border border-steel-300 bg-steel-100 p-3"
              @click="toggleSearchCollapsed"
            >
              <p class="font-normal text-steel-700">Search an origin</p>
              <img
                src="@/assets/right-arrow.svg"
                alt="arrow"
              />
              <p class="font-normal text-steel-700">Search a destination</p>
            </div>
          </div>

          <div
            v-else
            class="gap-2"
            :class="{
              'grid grid-cols-12': isDesktop,
              'flex flex-col': !isDesktop,
            }"
          >
            <!-- origin/destination -->
            <div
              class="col-span-7"
              :class="{
                'grid grid-cols-15': isDesktop,
                'flex flex-col gap-3': !isDesktop,
              }"
            >
              <div class="col-span-7">
                <p class="mb-1 text-xs font-semibold text-steel-800">Origin</p>
                <div class="flex">
                  <div class="flex-4 origin-input relative grow-[4]">
                    <img
                      class="absolute left-2 top-3 z-20 h-4 w-4"
                      src="@/assets/origin-marker.svg"
                      alt="origin pin"
                    />
                    <LocationSearchInput
                      type="origin"
                      placeholder="Search an Origin"
                      :input-value="filters.origin"
                      :show-icons="false"
                      :show-clear-icon="true"
                      :is-slim="false"
                      @set-location="setOriginFilters"
                      @set-selected-location="setSelectedOrigin"
                    ></LocationSearchInput>
                  </div>

                  <div
                    class="origin-dropdown relative w-full min-w-[80px] flex-1"
                  >
                    <dropdown-select
                      :options="deadheadOptions"
                      :default="selectedOriginDeadhead"
                      :value="selectedOriginDeadhead"
                      :exclude-selected="true"
                      class="deadhead-dropdown !h-12 border border-light-gray !bg-lighter-gray !bg-opacity-30"
                      @input="handleOriginDeadheadSelected"
                    />
                  </div>
                </div>
              </div>

              <div
                v-if="isDesktop"
                class="mt-auto flex cursor-pointer items-center bg-white p-2 hover:opacity-70"
                @click="toggleLocationInputOrder"
              >
                <img
                  class="mx-auto rotate-90"
                  src="@/assets/arrows-up-and-down.svg"
                  alt="arrows icon"
                />
              </div>

              <div
                :class="{
                  'col-span-7': isDesktop,
                  'col-span-8': !isDesktop,
                }"
              >
                <p class="mb-1 text-xs font-semibold text-steel-800">
                  Destination
                </p>

                <div class="flex">
                  <div class="flex-4 destination-input relative grow-[4]">
                    <img
                      class="absolute left-2 top-3 z-20 h-4 w-4"
                      src="@/assets/destination-gray.svg"
                      alt="destination pin"
                    />
                    <LocationSearchInput
                      type="destination"
                      placeholder="Search a Destination"
                      :input-value="filters.destination"
                      :show-icons="false"
                      :show-clear-icon="true"
                      :is-slim="false"
                      @set-location="setDestinationFilters"
                      @set-selected-location="setSelectedDestination"
                    ></LocationSearchInput>
                  </div>

                  <div
                    class="destination-dropdown relative w-full min-w-[80px] flex-1"
                  >
                    <dropdown-select
                      :options="deadheadOptions"
                      :default="selectedDestinationDeadhead"
                      :value="selectedDestinationDeadhead"
                      :exclude-selected="true"
                      class="deadhead-dropdown !h-12 border border-light-gray !bg-lighter-gray !bg-opacity-30 text-xs"
                      @input="handleDestinationDeadheadSelected"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- equipment/pickup -->
            <div
              class="col-span-5 flex gap-2"
              :class="{
                'flex-col': !isDesktop,
              }"
            >
              <div
                class="equipment-homepage flex w-full items-center gap-2"
                :class="{
                  'mb-2 flex-col': !isDesktop,
                }"
              >
                <div
                  class="w-full flex-1"
                  :class="{
                    'min-w-[140px]': isDesktop,
                  }"
                >
                  <p class="mb-1 text-xs font-semibold text-steel-800">
                    Equipment
                  </p>

                  <dropdown-select
                    :options="equipmentOptions"
                    :default="selectedEquipment"
                    :value="selectedEquipment"
                    :exclude-selected="true"
                    class="location-equipment !my-0 h-10 flex-1 border border-steel-300 !bg-white !bg-opacity-30 text-xs hover:border-steel-400"
                    @input="handleSelectedEquipment"
                  />
                </div>

                <div
                  class="location-pickup relative w-full flex-1"
                  :class="{
                    'min-w-[150px]': isDesktop,
                  }"
                >
                  <p
                    class="mb-1 min-w-max text-left text-xs font-semibold text-steel-800"
                  >
                    Pickup Date Range
                  </p>
                  <InputComponent
                    input-label="start_date"
                    wrapper-class="flex w-full items-center h-10 gap-1.5 px-2 cursor-auto location-date border rounded-md !border-steel-300"
                    placeholder="Select"
                    :default-input-value="pickupPlaceholder"
                    :value="pickupPlaceholder"
                    :show-clear-button="true"
                    :readonly="true"
                    :onlyUseWrapperClass="true"
                    @input-change="dynamicUpdatePickupDate"
                    @input-click="toggleShowPickupDatePicker"
                  />

                  <img
                    src="@/assets/calendar-light.svg"
                    class="location-calendar absolute right-2 top-7 cursor-pointer"
                    alt="calendar"
                    @click="toggleShowPickupDatePicker"
                  />

                  <div
                    v-show="showPickupDatePicker"
                    ref="calendarRef"
                    class="absolute right-0 top-full z-50 rounded-lg bg-white p-4 shadow-md"
                  >
                    <date-picker-range
                      :start-date="startPickupDate"
                      :end-date="endPickupDate"
                      :field="'pickup_datepicker'"
                      date-format="MM/DD/YYYY"
                      :disable-previous="true"
                      :restrict-previous="true"
                      @day-selected="setSelectedPickupDay"
                    />
                  </div>
                </div>
              </div>

              <ButtonComponent
                variant="primary"
                class="location-search-button mt-auto !h-10 !px-3"
                :disabled="isSearchDisabled"
                @click="handleSearch"
              >
                Search
              </ButtonComponent>
            </div>
          </div>
        </div>

        <div
          class="flex min-w-[290px] items-center rounded-lg bg-white p-4 shadow-md"
          :class="{
            'w-1/5': isDesktop,
            'w-full': !isDesktop,
            hidden:
              generalStatus.eld !== 'not-started' &&
              !canViewVerifiedPickupScore,
          }"
        >
          <div
            v-if="generalStatus.eld === 'not-started'"
            class="flex flex-col items-center gap-2"
          >
            <div class="flex items-center gap-1">
              <img
                src="@/assets/loads/wifi-gray.svg"
                alt="wifi"
              />

              <p class="text-lg text-steel-900">Connect your ELD</p>
            </div>
            <p class="text-xs font-normal text-steel-1100">
              Build trust and visibility to get more freight
            </p>

            <ButtonComponent
              variant="primary"
              class="mt-2 w-full"
            >
              Connect your ELD
            </ButtonComponent>
          </div>

          <div
            v-else
            class="flex w-full flex-col gap-4"
          >
            <div class="flex w-full items-center gap-1">
              <p class="min-w-[150px] text-steel-900">Verified Pickup Score</p>

              <Popper
                placement="top"
                :hover="true"
                offset-distance="6"
              >
                <img
                  class="cursor-pointer"
                  src="@/assets/info-black.svg"
                  alt="info"
                />
                <template #content>
                  <div class="max-w-56 rounded-md bg-dark-gray p-2 text-left">
                    <p class="text-xs text-steel-200">
                      Visit
                      <router-link
                        class="underline"
                        :to="{
                          name: 'ELDConnections',
                        }"
                      >
                        Equipment
                      </router-link>
                      to make sure all of your vehicles have their ELD connected
                      with Highway!
                    </p>
                  </div>
                </template>
              </Popper>
            </div>

            <div class="flex w-full items-center gap-4">
              <p class="text-3xl font-bold">{{ verifiedScore }}%</p>

              <div class="relative flex w-full flex-col items-start gap-2">
                <p class="font-normal uppercase">consistent</p>

                <img
                  class="absolute top-5"
                  :style="`left: calc(${verifiedScore}% - 10px)`"
                  src="@/assets/triangle-black.svg"
                  alt="arrow"
                />
                <span class="score-bar flex h-4 w-full rounded-lg"></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LoadsCarousel
        :view-all-redirect="viewAllRecommendedLoads"
      ></LoadsCarousel>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import dayjs from 'dayjs';
import Popper from 'vue3-popper';
import {
  ButtonComponent,
  InputComponent,
  DatePickerRange,
  LocationSearchInput,
  LoadsCarousel,
  LoadDropdown as DropdownSelect,
} from '@/components';
import { useExchangeAccess, useScreenSize } from '@/composables';
import { createFilterQuery, useClickAway } from '@/utils';

const store = useStore();
const router = useRouter();
const toast = useToast();
const { innerWidth } = useScreenSize();
const { displayEquipmentModal, generalStatus } = useExchangeAccess();

const isDesktop = computed(() => innerWidth.value >= 1250);

const verifiedScore = computed(() => 80);

const deadheadOptions = [
  '50 mi',
  '100 mi',
  '150 mi',
  '200 mi',
  '250 mi',
  '300 mi',
  '350 mi',
  '400 mi',
  '450 mi',
  '500 mi',
];

const equipmentOptions = [
  'All Equipment',
  'Flatbed',
  'Power Only',
  'Reefer',
  'Van',
];

const isEquipmentModalVisible = ref(false);

const searchCollapsed = ref(true);
const toggleSearchCollapsed = () => {
  searchCollapsed.value = false;
};

const canViewVerifiedPickupScore = computed(
  () =>
    store.state.users.featureFlags?.fe_carrier_verified_pickup_score?.enabled
);

onMounted(async () => {
  isEquipmentModalVisible.value = displayEquipmentModal.value;

  store.dispatch('loads/getRecommendedLoads', { type: 'user_recommendations' });
});

const filters = ref({
  origin: '',
  destination: '',
  selectedOrigin: '',
  selectedDestination: '',
  deadhead: '',
  city_state_origin: '',
  city_state_destination: '',
  origin_deadhead: '150mi',
  destination_deadhead: '150mi',
  equipment: [],
});

const viewAllRecommendedLoads = async () => {
  await store.commit('loads/setLoads', {
    results: store.state.loads?.recommended_loads?.loads,
  });
  await store.commit('loads/setSearchLoadError', {});
  await store.commit('loads/setIsRecommendedView', true);
  await store.commit('loads/setLoadSearchFilters', {
    origin: '',
    destination: '',
    selectedOrigin: '',
    selectedDestination: '',
    deadhead: '',
    city_state_origin: '',
    city_state_destination: '',
    origin_deadhead: '150',
    destination_deadhead: '150',
    equipment: [],
  });
  await store.commit(
    'loads/setLoadboardPagination',
    store.state.loads?.recommended_loads_pagination
  );

  await router.push({ name: 'Load Board' });

  toast.open({
    message: 'Showing Recommended Loads',
    position: 'top-right',
    duration: 5000,
  });
};

const isSearchDisabled = computed(
  () => !filters.value?.origin?.length && !filters.value?.destination?.length
);

const selectedOriginDeadhead = ref('150mi');
const selectedDestinationDeadhead = ref('150mi');
const handleOriginDeadheadSelected = (item) => {
  console.log('select deadhead', item);
  const origin = item?.split(' ')?.[0];
  filters.value.origin_deadhead = origin;
  selectedOriginDeadhead.value = `${origin}mi`;
};
const handleDestinationDeadheadSelected = (item) => {
  console.log('select deadhead', item);
  const destination = item?.split(' ')?.[0];
  filters.value.destination_deadhead = destination;
  selectedDestinationDeadhead.value = `${destination}mi`;
};

const setOriginFilters = (origin) => {
  filters.value.origin = origin;
};

const setDestinationFilters = (destination) => {
  filters.value.destination = destination;
};

const setSelectedOrigin = (origin) => {
  filters.value.origin_area_id = origin?.id;
  filters.value.selectedOrigin = origin?.code;
};

const setSelectedDestination = (destination) => {
  filters.value.destination_area_id = destination?.id;
  filters.value.selectedDestination = destination?.code;
};

const equipmentMap = {
  Flatbed: 'flatbed',
  'Power Only': 'power_only',
  Reefer: 'reefer',
  Van: 'van',
};

const selectedEquipment = ref('All Equipment');
const handleSelectedEquipment = (item) => {
  selectedEquipment.value = item;
  console.log('select equipment', item);

  if (item !== 'All Equipment') {
    filters.value.equipment = [equipmentMap[item]];
  }
};

const selectedPickupDate = ref(filters.value.pickup_start || '');
const selectedPickupEndDate = ref(filters.value.pickup_end || '');
const showPickupDatePicker = ref(false);

const pickupPlaceholder = computed(() => {
  if (filters.value.pickup_start && filters.value.pickup_end) {
    return `${dayjs(filters.value.pickup_start).format('MMM DD')} - ${dayjs(filters.value.pickup_end).format('MMM DD')}`;
  } else {
    return '';
  }
});

const toggleShowPickupDatePicker = () => {
  showPickupDatePicker.value = !showPickupDatePicker.value;
};

function dynamicUpdatePickupDate({ value }) {
  if (value) {
    filters.value.pickup_start = selectedPickupDate.value;
    filters.value.pickup_end = selectedPickupEndDate.value;
  }
}

function setSelectedPickupDay(fieldValue, dateSelected) {
  if (
    !selectedPickupDate.value ||
    (selectedPickupDate.value && selectedPickupEndDate.value)
  ) {
    // set pickup start range
    selectedPickupDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
    selectedPickupEndDate.value = undefined;
  } else {
    // set pickup end range
    selectedPickupEndDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
    dynamicUpdatePickupDate({ name: fieldValue, value: dateSelected });
    showPickupDatePicker.value = false;
  }
}

const calendarRef = ref();
useClickAway(calendarRef, (e) => {
  if (
    !e.target.classList?.contains('datepicker-btn') &&
    !e.target.classList?.contains('datepicker-calendar-icon')
  ) {
    showPickupDatePicker.value = false;
  }
});

const handleSearch = async () => {
  console.log('handle search:');

  await store.commit('loads/setLoads', []);
  await store.commit('loads/setLoadSearchFilters', filters.value);

  const params = createFilterQuery(
    filters.value,
    router.currentRoute.value.query
  );

  // redirect to load-board route
  await router.push({
    name: 'Load Board',
    query: params,
  });
};

const startPickupDate = computed(() => {
  return selectedPickupDate.value
    ? dayjs(selectedPickupDate.value).format('YYYY-MM-DD')
    : undefined;
});
const endPickupDate = computed(() => {
  return selectedPickupEndDate.value
    ? dayjs(selectedPickupEndDate.value).format('YYYY-MM-DD')
    : undefined;
});

const toggleLocationInputOrder = () => {
  const previousOrigin = filters.value.origin;
  const previousDestination = filters.value.destination;
  const previousOriginDeadhead = filters.value.origin_deadhead;
  const previousDestinationDeadhead = filters.value.destination_deadhead;
  const previousSelectedOrigin = filters.value.selectedOrigin;
  const previousSelectedDestination = filters.value.selectedDestination;
  const previousOriginAreaId = filters.value.origin_area_id;
  const previousDestinationAreaId = filters.value.destination_area_id;

  const updatedFilters = {
    ...filters.value,
    origin: previousDestination,
    destination: previousOrigin,
    origin_deadhead: previousDestinationDeadhead,
    destination_deadhead: previousOriginDeadhead,
    selectedOrigin: previousSelectedDestination,
    selectedDestination: previousSelectedOrigin,
    origin_area_id: previousDestinationAreaId,
    destination_area_id: previousOriginAreaId,
  };

  filters.value = updatedFilters;
  selectedOriginDeadhead.value = previousDestinationDeadhead;
  selectedDestinationDeadhead.value = previousOriginDeadhead;
};
</script>

<style>
.origin-input input {
  @apply h-10 rounded-lg rounded-r-none bg-white pl-7 text-xs;
}
.origin-input input:hover,
.destination-input input:hover {
  @apply bg-steel-100;
}

.origin-dropdown > div {
  @apply my-0 !h-10 rounded-lg rounded-l-none border-none;
}
.origin-dropdown > div > div:nth-of-type(1) {
  @apply border border-steel-300 !px-2;
}
.origin-dropdown > div > div:nth-of-type(1):hover,
.destination-dropdown > div > div:nth-of-type(1):hover {
  @apply bg-steel-200;
}
.origin-dropdown > div > div:nth-of-type(2) {
  @apply border border-steel-300;
}
.origin-dropdown p,
.destination-dropdown p,
.origin-dropdown > div > div {
  @apply text-left !text-xs;
}
.origin-dropdown > div > div:nth-of-type(2) > div,
.destination-dropdown > div > div:nth-of-type(2) > div {
  @apply !px-1;
}

.origin-input div div img,
.destination-input div div img {
  @apply top-[16px];
}
.destination-input input {
  @apply h-10 rounded-lg rounded-r-none bg-white pl-7 text-xs;
}
.destination-dropdown > div {
  @apply my-0 !h-10 rounded-lg rounded-l-none border-none;
}
.destination-dropdown > div > div:nth-of-type(1) {
  @apply border border-steel-300 !px-2;
}
.destination-dropdown > div > div:nth-of-type(2) {
  @apply border border-steel-300;
}

.location-date > div:nth-of-type(1) {
  @apply right-10;
}

.location-pickup:hover .location-date {
  @apply !border-steel-400;
}

.location-equipment p {
  @apply !text-xs;
}
.location-equipment > div {
  @apply !bg-white;
}

.equipment-homepage > div {
  @apply my-0 rounded-lg;
}

.equipment-homepage p {
  @apply text-left text-xs;
}
.equipment-homepage > div > div:first-of-type {
  @apply bg-opacity-30;
}

.score-bar {
  background: linear-gradient(
    90deg,
    #ff3001 15.5%,
    #f69320 36.65%,
    #edb13e 57.79%,
    #52d060 85.5%
  );
}

.header-texture {
  background-image:
    radial-gradient(
      60% 50% at 50% 50%,
      rgba(41, 48, 61, 0) 0%,
      var(--color-steel-1100) 100%
    ),
    url('@/assets/header-texture.svg');

  @apply bg-steel-1100;
}

/* firefox */
.loads-homepage-container {
  scrollbar-width: thin; /* thin scrollbar */
  scrollbar-color: transparent transparent; /* transparent by default */
}

.loads-homepage-container:hover {
  scrollbar-color: #888 #f1f1f1; /* visible on hover */
}

/* chrome */
.loads-homepage-container::-webkit-scrollbar {
  width: 8px; /* default width */
  background: transparent; /* hide by default */
}

.loads-homepage-container::-webkit-scrollbar-thumb {
  background-color: transparent; /* hide thumb */
  border-radius: 4px;
}

.loads-homepage-container:hover::-webkit-scrollbar-thumb {
  background-color: #888; /* show thumb on hover */
}

.location-date > input {
  @apply w-[inherit] text-xs !text-steel-800;
}
</style>
